import React, {useEffect, useRef, useState} from 'react';
import {Box, Tab, Tabs} from '@mui/material';
import './TabsCommonComponent.scss';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number | string;
  value: number | string;
  label: string;
  onTabCycle: (index: number | string) => void;
}

export interface ITabsData {
  id: number | string;
  label: string;
  content: React.ReactNode;
}

interface ITabsCommonComponent {
  tabsData: ITabsData[];
  defaultActiveTab?: ITabsData;
  aria_label?: string;
}

function TabPanel({
  children,
  value,
  index,
  label,
  onTabCycle,
  ...other
}: Readonly<TabPanelProps>) {
  const panelRef = useRef<HTMLDivElement>(null);

  const getFocusableElements = () => {
    if (!panelRef.current) return [];
    return Array.from(
      panelRef.current.querySelectorAll<HTMLElement>(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('disabled'));
  };

  const handleTabCycle = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || e.shiftKey) return;
    const focusable = getFocusableElements();
    if (focusable.length === 0) return;
    const lastElement = focusable[focusable.length - 1];
    if (document.activeElement === lastElement) {
      e.preventDefault();
      onTabCycle(index);
    }
  };

  return (
    <div
      id={`review-tabpanel-${index}`}
      aria-labelledby={`review-tab-${index}`}
      data-testid={`tabs-common-component-div-${index}`}
      role="tabpanel"
      hidden={value !== index}
      ref={panelRef}
      onKeyDown={handleTabCycle}
      {...other}
    >
      {value === index && (
        <Box
          className="tab-panel-content"
          data-testid={`tabs-common-component-box-${index}`}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

const TabsCommonComponent = ({
  tabsData,
  defaultActiveTab,
  aria_label = 'Section navigation',
}: ITabsCommonComponent) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (defaultActiveTab) {
      const index = tabsData.findIndex((tab) => tab.id === defaultActiveTab.id);
      if (index !== -1) setActiveTabIndex(index);
    }
  }, [defaultActiveTab, tabsData]);

  const handleTabChange = (_: React.SyntheticEvent, newIndex: number) => {
    setActiveTabIndex(newIndex);
  };

  const handleTabCycle = (index: number | string) => {
    if (typeof index === 'number') {
      const nextTabIndex = index + 1 < tabsData.length ? index + 1 : 0;
      setActiveTabIndex(nextTabIndex);
    }
  };

  useEffect(() => {
    if (tabRefs.current[activeTabIndex]) {
      tabRefs.current[activeTabIndex]?.focus();
    }
  }, [activeTabIndex]);

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    const lastIndex = tabsData.length - 1;
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        setActiveTabIndex(index === lastIndex ? 0 : index + 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        setActiveTabIndex(index === 0 ? lastIndex : index - 1);
        break;
      case 'Home':
        event.preventDefault();
        setActiveTabIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveTabIndex(lastIndex);
        break;
      default:
        break;
    }
  };

  return (
    <Box className="review-tabs-container">
      <Box className="review-tabs-section" data-testid="tabs-section">
        <Tabs
          value={activeTabIndex}
          onChange={handleTabChange}
          aria-label={aria_label}
          className="review-tabs-content"
          data-testid="tabs"
        >
          {tabsData.map((tab, index) => (
            <Tab
              key={tab.id}
              label={tab.label}
              // Removed manual id and aria-controls to prevent MUI conflict
              className="review-tab-content"
              data-testid={`tab-${tab.id}`}
              ref={(el) => {
                tabRefs.current[index] = el as HTMLButtonElement | null;
              }}
              onKeyDown={(e) => handleKeyDown(e, index)}
            />
          ))}
        </Tabs>
      </Box>

      <Box className="review-tabs-child">
        {tabsData.length > 0 && (
          <TabPanel
            key={tabsData[activeTabIndex].id}
            value={activeTabIndex}
            index={activeTabIndex}
            label={tabsData[activeTabIndex].label}
            onTabCycle={handleTabCycle}
            data-testid={`tabpanel-${tabsData[activeTabIndex].id}`}
          >
            {tabsData[activeTabIndex].content}
          </TabPanel>
        )}
      </Box>
    </Box>
  );
};

export default TabsCommonComponent;
